import * as Sentry from '@sentry/react';
import axios from 'axios';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import useSWRImmutable from 'swr/immutable';
import {
  AccountView,
  BeneficiaryView,
  CardView,
  CategoryView,
  CompanyView,
  DocumentView,
  EmployeeView,
  InvoiceEvidenceView,
  OrganisationView,
  PayrollRecordView,
  PayrollView,
  TransactionView,
  VendorView,
} from '../../types/entity-types';
import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { useSSE } from '../sse-context/use-sse';
import { useUserInfo } from '../user-context/use-user-info';
import { checkDuplicateTransactions } from './check-duplicate-transactions';
import { getLastUpdated } from './get-last-updated';
import { getLinkedData } from './get-linked-data';
import { getSortedData } from './get-sorted-data';
import { isAiEnrichmentInProgress } from './is-ai-enrichment-in-progress';
import { syncDeleteUpdates } from './sync-delete-updates';
import { syncIncrementalUpdates } from './sync-incremental-updates';
import { useBaseCurrency } from './use-base-currency';
import { useFxData } from './use-fx';

export const BASE_API_PATH = '/api/entities/by-type';

const ORGANISATION_API_PATH = `${BASE_API_PATH}/organisation`;
export const COMPANY_API_PATH = `/api/companies`;
export const ACCOUNT_API_PATH = `${BASE_API_PATH}/baseaccount`;
// Using custom transactions endpoint that includes eager-loaded events for timeline display
export const TRANSACTION_API_PATH = `/api/transactions`;
export const VENDOR_API_PATH = `${BASE_API_PATH}/vendor`;
export const CATEGORY_API_PATH = `${BASE_API_PATH}/category`;
// Using custom evidence endpoint that includes eager-loaded external references and sync status
export const EVIDENCE_API_PATH = `/api/evidence/invoice`;
// TODO: when we support multiple companies per organisation, we need to add a companyId param to the beneficiary API path
export const BENEFICIARY_API_PATH = `/api/beneficiaries`;
export const CARD_API_PATH = `${BASE_API_PATH}/card`;
export const DOCUMENT_API_PATH = `${BASE_API_PATH}/document`;
export const EMPLOYEE_API_PATH = `/api/employee`;
export const PAYROLL_API_PATH = `${BASE_API_PATH}/payroll`;
export const PAYROLL_RECORD_API_PATH = `${BASE_API_PATH}/payrollrecord`;

export type DataEntities = {
  organisations: OrganisationView[];
  companies: CompanyView[];
  accounts: AccountView[];
  cards: CardView[];
  transactions: TransactionView[];
  vendors: VendorView[];
  categories: CategoryView[];
  evidence: InvoiceEvidenceView[];
  beneficiaries: BeneficiaryView[];
  documents: DocumentView[];
  employees: EmployeeView[];
  payrolls: PayrollView[];
  payrollRecords: PayrollRecordView[];
};

type BaseCurrency = {
  baseCurrency: SupportedCountryCurrency;
};

type MutableEntities = Partial<Omit<DataEntities, 'organisations'>>;

type DataContextData = DataEntities & BaseCurrency;

export type DataContextValue = {
  data?: DataContextData;
  isLoading: boolean;
  error?: Error;
  mutations: {
    mutateAll: () => Promise<void>;
    mutateCategories?: KeyedMutator<CategoryView[]>;
    mutateAccounts?: KeyedMutator<AccountView[]>;
    mutateCache?: (cacheData: MutableEntities) => Promise<void>;
    setBaseCurrency: (currency: SupportedCountryCurrency) => void;
    handleIncrementalUpdates: () => Promise<void>;
    handleDeleteUpdates: (
      deletedEntityDetails: Parameters<typeof syncDeleteUpdates>[0]['deletedEntityDetails'],
    ) => Promise<void>;
  };
  meta?: {
    lastUpdated?: Record<keyof DataEntities, string>;
    isAiEnrichmentInProgress: boolean;
  };
};

const DataContext = createContext<DataContextValue | null>(null);

class SeapointAPI<T> {
  private apiPath: string;
  private data: T | null;

  constructor(apiPath: string) {
    this.apiPath = apiPath;
    this.data = null;
  }

  async list({ url }: { url: string }) {
    // NOTE: Removed this caching for now because it interferes with SWR's revalidation
    // if (this.data) return this.data;

    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.get<T>(url, options);
    this.data = data;

    return this.data;
  }

  getApiPath() {
    return this.apiPath;
  }
}

type SWRKey = { url: string } | null;

function createApiHook<T>(api: SeapointAPI<T>) {
  return (shouldFetchData: boolean) => {
    const url = api.getApiPath();

    // SWR will only fetch data if shouldFetchData is true
    // useSWRImmutable disables auto revalidation
    // Seapoint data should only revalidate when the user syncs accounts or on load
    // This is the same as useSWR with the following options: revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false
    // https://swr.vercel.app/docs/revalidation.en-US#disable-automatic-revalidations
    return useSWRImmutable<T, Error, SWRKey>(
      shouldFetchData
        ? {
            url,
          }
        : null,
      // Using bind() to preserve the 'this' context of the api instance
      // Alternative approach: Using arrow function to capture api in closure: (args) => api.list(args)
      api.list.bind(api),
    );
  };
}

const useOrganisations = createApiHook<OrganisationView[]>(new SeapointAPI(ORGANISATION_API_PATH));
const useCompanies = createApiHook<CompanyView[]>(new SeapointAPI(COMPANY_API_PATH));
const useAccounts = createApiHook<AccountView[]>(new SeapointAPI(ACCOUNT_API_PATH));
const useTransactions = createApiHook<TransactionView[]>(new SeapointAPI(TRANSACTION_API_PATH));
const useVendors = createApiHook<VendorView[]>(new SeapointAPI(VENDOR_API_PATH));
const useCategories = createApiHook<CategoryView[]>(new SeapointAPI(CATEGORY_API_PATH));
const useEvidence = createApiHook<InvoiceEvidenceView[]>(new SeapointAPI(EVIDENCE_API_PATH));
const useBeneficiaries = createApiHook<BeneficiaryView[]>(new SeapointAPI(BENEFICIARY_API_PATH));
const useCards = createApiHook<CardView[]>(new SeapointAPI(CARD_API_PATH));
const useDocuments = createApiHook<DocumentView[]>(new SeapointAPI(DOCUMENT_API_PATH));
const useEmployees = createApiHook<EmployeeView[]>(new SeapointAPI(EMPLOYEE_API_PATH));
const usePayrolls = createApiHook<PayrollView[]>(new SeapointAPI(PAYROLL_API_PATH));
const usePayrollRecords = createApiHook<PayrollRecordView[]>(new SeapointAPI(PAYROLL_RECORD_API_PATH));

type DataProviderProps = {
  children: ReactNode;
};

export const DataProvider = ({ children }: DataProviderProps) => {
  const { isAuthenticated, authenticatedInfo } = useUserInfo();
  const { lastRefreshEvent } = useSSE();

  // Using refs to avoid closures problems when calling handleIncrementalUpdates and handleDeleteUpdates
  // https://www.developerway.com/posts/fantastic-closures
  const syncIncrementalUpdatesRef = useRef<typeof handleIncrementalUpdates>();
  const syncDeleteUpdatesRef = useRef<typeof handleDeleteUpdates>();

  // Data should only be fetched if the user is authenticated, their email is verified and the user has an organisation
  const shouldFetchData = Boolean(isAuthenticated && authenticatedInfo?.emailVerified && authenticatedInfo?.isOrgReady);

  const {
    data: organisations,
    // isValidating becomes true whenever there is an ongoing request whether the data is loaded or not
    // Source: https://swr.vercel.app/docs/advanced/understanding.en-US#combining-with-isloading-and-isvalidating-for-better-ux
    isValidating: isFetchingOrganisations,
    error: errorOrganisations,
    mutate: mutateOrganisations,
  } = useOrganisations(shouldFetchData);
  const {
    data: companies,
    isValidating: isFetchingCompanies,
    error: errorCompanies,
    mutate: mutateCompanies,
  } = useCompanies(shouldFetchData);
  const {
    data: accounts,
    isValidating: isFetchingAccounts,
    error: errorAccounts,
    mutate: mutateAccounts,
  } = useAccounts(shouldFetchData);
  const {
    data: transactions,
    isValidating: isFetchingTransactions,
    error: errorTransactions,
    mutate: mutateTransactions,
  } = useTransactions(shouldFetchData);
  const {
    data: vendors,
    isValidating: isFetchingVendors,
    error: errorVendors,
    mutate: mutateVendors,
  } = useVendors(shouldFetchData);
  const {
    data: categories,
    isValidating: isFetchingCategories,
    error: errorCategories,
    mutate: mutateCategories,
  } = useCategories(shouldFetchData);
  const {
    data: evidence,
    isValidating: isFetchingEvidence,
    error: errorEvidence,
    mutate: mutateEvidence,
  } = useEvidence(shouldFetchData);
  const {
    data: beneficiaries,
    isValidating: isFetchingBeneficiaries,
    error: errorBeneficiaries,
    mutate: mutateBeneficiaries,
  } = useBeneficiaries(shouldFetchData);
  const {
    data: cards,
    isValidating: isFetchingCards,
    error: errorCards,
    mutate: mutateCards,
  } = useCards(shouldFetchData);
  const {
    data: documents,
    isValidating: isFetchingDocuments,
    error: errorDocuments,
    mutate: mutateDocuments,
  } = useDocuments(shouldFetchData);
  const {
    data: employees,
    isValidating: isFetchingEmployees,
    error: errorEmployees,
    mutate: mutateEmployees,
  } = useEmployees(shouldFetchData);
  const {
    data: payrolls,
    isValidating: isFetchingPayrolls,
    error: errorPayrolls,
    mutate: mutatePayrolls,
  } = usePayrolls(shouldFetchData);
  const {
    data: payrollRecords,
    isValidating: isFetchingPayrollRecords,
    error: errorPayrollRecords,
    mutate: mutatePayrollRecords,
  } = usePayrollRecords(shouldFetchData);

  // Derive base currency from company data
  const derivedBaseCurrency = useBaseCurrency(companies, shouldFetchData);

  // Allow users to override the base currency via the currency selector
  const [baseCurrency, setBaseCurrency] = useState<SupportedCountryCurrency | undefined>();

  // Initialize with derived currency when it becomes available (only once)
  useEffect(() => {
    if (derivedBaseCurrency && !baseCurrency) {
      setBaseCurrency(derivedBaseCurrency);
    }
  }, [derivedBaseCurrency, baseCurrency]);

  const {
    data: fxData,
    isLoading: isFetchingFxData,
    error: errorFxData,
  } = useFxData({
    transactions,
    evidence,
    baseCurrency,
    shouldFetchData: shouldFetchData && !!baseCurrency, // Only fetch FX data when we have base currency
  });

  const isFetchingCurrency = shouldFetchData && !baseCurrency;

  const rawData: Partial<DataEntities> = {
    organisations,
    companies,
    accounts,
    cards,
    transactions,
    categories,
    vendors,
    evidence,
    beneficiaries,
    documents,
    employees,
    payrolls,
    payrollRecords,
  };

  const isLoadingData =
    isFetchingOrganisations ||
    isFetchingCompanies ||
    isFetchingAccounts ||
    isFetchingCards ||
    isFetchingTransactions ||
    isFetchingVendors ||
    isFetchingCategories ||
    isFetchingEvidence ||
    isFetchingBeneficiaries ||
    isFetchingFxData ||
    isFetchingCurrency ||
    isFetchingDocuments ||
    isFetchingEmployees ||
    isFetchingPayrolls ||
    isFetchingPayrollRecords;

  const errorData =
    errorOrganisations ||
    errorCompanies ||
    errorAccounts ||
    errorCards ||
    errorTransactions ||
    errorCategories ||
    errorVendors ||
    errorEvidence ||
    errorBeneficiaries ||
    errorDocuments ||
    errorEmployees ||
    errorPayrolls ||
    errorPayrollRecords ||
    errorFxData;

  const shouldNotLinkData =
    // We don't need to link data if:
    // 1. Data is loading
    isLoadingData ||
    // 2. There is an error fetching the data
    errorData ||
    // 3. The user has no company yet (they are in signup stage)
    !rawData.companies?.length ||
    // 4. We don't have base currency yet
    !baseCurrency ||
    // 5. useFxData should always return a value. We use !fxData to narrow down the type to ensure type safety
    !fxData;

  if (shouldNotLinkData && shouldFetchData) {
    const reasons = [];
    if (isLoadingData) reasons.push('data_is_loading');
    if (errorData) reasons.push('has_error_data');
    if (!rawData.companies?.length) reasons.push('no_companies');
    if (!baseCurrency) reasons.push('no_base_currency');
    if (!fxData) reasons.push('no_fx_data');

    Sentry.addBreadcrumb({
      category: 'data_provider',
      message: 'Skipping data linking',
      level: 'info',
      data: {
        reasons,
        rawDataCounts: {
          organisations: rawData.organisations?.length || 0,
          companies: rawData.companies?.length || 0,
          accounts: rawData.accounts?.length || 0,
          cards: rawData.cards?.length || 0,
          transactions: rawData.transactions?.length || 0,
          vendors: rawData.vendors?.length || 0,
          categories: rawData.categories?.length || 0,
          evidence: rawData.evidence?.length || 0,
          beneficiaries: rawData.beneficiaries?.length || 0,
          documents: rawData.documents?.length || 0,
          employees: rawData.employees?.length || 0,
          payrolls: rawData.payrolls?.length || 0,
          payrollRecords: rawData.payrollRecords?.length || 0,
        },
        isLoadingData,
        errorData: errorData?.message || 'none',
        hasFxData: !!fxData,
        hasBaseCurrency: !!baseCurrency,
        shouldFetchData,
      },
    });
  }

  const linkedData = shouldNotLinkData ? undefined : getLinkedData(rawData, fxData);

  checkDuplicateTransactions(linkedData?.transactions);

  const sortedData = linkedData && getSortedData(linkedData);

  // Revalidates provider data. Needed after an entity has been updated.
  // https://swr.vercel.app/docs/mutation#revalidation
  const mutateAll = async () => {
    await Promise.all([
      mutateOrganisations(),
      mutateCompanies(),
      mutateAccounts(),
      mutateCards(),
      mutateTransactions(),
      mutateCategories(),
      mutateVendors(),
      mutateEvidence(),
      mutateBeneficiaries(),
      mutateEmployees(),
      mutatePayrolls(),
      mutatePayrollRecords(),
    ]);
  };

  const meta = {
    lastUpdated: sortedData && getLastUpdated(sortedData),
    isAiEnrichmentInProgress: sortedData?.transactions ? isAiEnrichmentInProgress(sortedData?.transactions) : false,
  };

  const mutateCache = async (newCacheData: MutableEntities) => {
    Object.entries(newCacheData).forEach(async ([entityName]) => {
      // Object.entries return type is [string, T][] so we need to cast it with the name of the entities so the switch below with exhaustive check below works properly
      const cacheDataKey = entityName as keyof MutableEntities;

      if (!newCacheData[cacheDataKey]) {
        return;
      }

      switch (cacheDataKey) {
        case 'companies':
          await mutateSWRCache(mutateCompanies, newCacheData[cacheDataKey]);

          return;
        case 'accounts':
          await mutateSWRCache(mutateAccounts, newCacheData[cacheDataKey]);

          return;
        case 'transactions':
          await mutateSWRCache(mutateTransactions, newCacheData[cacheDataKey]);

          return;
        case 'vendors':
          await mutateSWRCache(mutateVendors, newCacheData[cacheDataKey]);

          return;
        case 'categories':
          await mutateSWRCache(mutateCategories, newCacheData[cacheDataKey]);

          return;
        case 'evidence':
          await mutateSWRCache(mutateEvidence, newCacheData[cacheDataKey]);

          return;
        case 'beneficiaries':
          await mutateSWRCache(mutateBeneficiaries, newCacheData[cacheDataKey]);

          return;
        case 'cards':
          await mutateSWRCache(mutateCards, newCacheData[cacheDataKey]);

          return;
        case 'documents':
          await mutateSWRCache(mutateDocuments, newCacheData[cacheDataKey]);

          return;
        case 'employees':
          await mutateSWRCache(mutateEmployees, newCacheData[cacheDataKey]);

          return;
        case 'payrolls':
          await mutateSWRCache(mutatePayrolls, newCacheData[cacheDataKey]);

          return;
        case 'payrollRecords':
          await mutateSWRCache(mutatePayrollRecords, newCacheData[cacheDataKey]);

          return;
        default:
          return exhaustiveCheck(cacheDataKey);
      }
    });
  };

  const handleIncrementalUpdates = async () => {
    await syncIncrementalUpdatesRef.current?.();
  };

  const handleDeleteUpdates = async (
    deletedEntityDetails: Parameters<typeof syncDeleteUpdates>[0]['deletedEntityDetails'],
  ) => {
    await syncDeleteUpdatesRef.current?.(deletedEntityDetails);
  };

  useEffect(() => {
    const triggerIncrementalUpdates = async () => {
      if (lastRefreshEvent && sortedData) {
        await handleIncrementalUpdates();
      }
    };

    triggerIncrementalUpdates();

    // We disable the exhaustive-deps rule because we only want to sync incremental updates when the lastRefreshEvent changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRefreshEvent]);

  useEffect(() => {
    syncIncrementalUpdatesRef.current = async () =>
      await syncIncrementalUpdates({
        mutateCache,
        lastUpdated: meta.lastUpdated,
        entityData: sortedData,
      });

    syncDeleteUpdatesRef.current = async (
      deletedEntityDetails: Parameters<typeof syncDeleteUpdates>[0]['deletedEntityDetails'],
    ) =>
      await syncDeleteUpdates({
        mutateCache,
        entityData: {
          accounts: sortedData?.accounts,
          transactions: sortedData?.transactions,
          vendors: sortedData?.vendors,
          evidence: sortedData?.evidence,
          documents: sortedData?.documents,
          payrolls: sortedData?.payrolls,
          payrollRecords: sortedData?.payrollRecords,
          beneficiaries: sortedData?.beneficiaries,
        },
        deletedEntityDetails,
      });

    // No dependencies in this effect because we want to run it on every render
    // This avoids sortedData closures problems when calling handleIncrementalUpdates and handleDeleteUpdates across the app
    // https://www.developerway.com/posts/fantastic-closures
  });

  // Mutate all cache data when the active organization ID doesn't match the cached organization ID
  // This handles cases where multiple tabs are open with different active organizations
  // and ensures data consistency across tabs
  if (authenticatedInfo?.activeOrg && organisations?.[0].id) {
    if (authenticatedInfo?.activeOrg.id !== organisations?.[0]?.id) {
      mutateAll();

      return;
    }
  }

  if (!sortedData && !isLoadingData && shouldFetchData) {
    Sentry.addBreadcrumb({
      category: 'data_provider',
      message: 'Returning undefined data to components',
      level: 'warning',
      data: {
        linkedDataExists: !!linkedData,
        shouldNotLinkData,
        errorData: errorData?.message || 'none',
        baseCurrency,
        authenticatedInfo: {
          isAuthenticated,
          emailVerified: authenticatedInfo?.emailVerified,
          isOrgReady: authenticatedInfo?.isOrgReady,
        },
      },
    });
  }

  const value: DataContextValue = {
    data: sortedData && baseCurrency ? { ...sortedData, baseCurrency } : undefined,
    isLoading: isLoadingData,
    error: errorData,
    mutations: {
      mutateAll,
      setBaseCurrency,
      mutateCategories,
      mutateAccounts,
      mutateCache,
      handleIncrementalUpdates,
      handleDeleteUpdates,
    },
    meta,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

DataProvider.DataContext = DataContext;

/**
 * Helper function to update SWR cache without triggering a revalidation
 * @param mutator - SWR's mutate function for a specific entity type
 * @param cacheData - New data to update the cache with
 * @returns Promise that resolves when the cache is updated
 * @example
 * await mutateSWRCache(mutateTransactions, newTransactions);
 */
const mutateSWRCache = <T,>(mutator: KeyedMutator<T>, cacheData: T) => {
  return mutator(cacheData, { revalidate: false });
};
